/* VSS Header 4.0 — two rows, native account / wishlist / cart, full-width results. */
(() => {
  if (window.VSSCompactHeader) { window.VSSCompactHeader.refresh(); return; }

  class CompactHeader {
    constructor(header) {
      this.header = header;
      this.grid = header.querySelector('.header__grid');
      this.icons = header.querySelector('.header__icons');
      this.menu = header.querySelector('main-menu');
      this.search = header.querySelector('.header__search .search');
      this.input = this.search?.querySelector('input[name="q"]');
      this.originalPlaceholder = this.input?.getAttribute('placeholder');
      this.media = window.matchMedia('(min-width: 769px)');
      this.moves = new Map();
      this.schedule = this.schedule.bind(this);
      this.closeSearch = this.closeSearch.bind(this);
      this.onScroll = () => {
        if (this.header.querySelector('predictive-search[open]')) this.schedule();
      };
      this.media.addEventListener('change', this.schedule);
      window.addEventListener('resize', this.schedule, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      header.addEventListener('load', this.schedule, true);
      this.search?.addEventListener('focusin', this.schedule);
      this.menu?.addEventListener('pointerover', this.closeSearch);
      this.menu?.addEventListener('focusin', this.closeSearch);
      this.iconObserver = new MutationObserver(this.schedule);
      if (this.icons) this.iconObserver.observe(this.icons, { childList: true });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(header);
      const announcement = document.querySelector('.cc-announcement');
      if (announcement) this.resizeObserver.observe(announcement);
      document.fonts?.ready.then(() => { if (!this.destroyed) this.schedule(); });
      this.update();
    }

    schedule() {
      if (this.destroyed || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }

    closeSearch() {
      if (!this.media.matches) return;
      const predictive = this.header.querySelector('predictive-search[open]');
      if (predictive && typeof predictive.close === 'function') predictive.close();
    }

    move(node, target = this.dock, before = null) {
      if (!node || this.moves.has(node)) return;
      const marker = document.createComment('vss-utility-return');
      node.before(marker);
      this.moves.set(node, { marker, target });
      // Move existing nodes, preserving account/app handlers and cart state.
      target.insertBefore(node, before);
    }

    restore() {
      for (const [node, { marker, target }] of this.moves) {
        // A wishlist app may remove/recreate its node. Never resurrect a removed copy.
        if (target === this.icons && !node.isConnected) marker.remove();
        else if (marker.parentNode) marker.replaceWith(node);
      }
      this.moves.clear();
      if (this.input) {
        if (this.originalPlaceholder == null) this.input.removeAttribute('placeholder');
        else this.input.setAttribute('placeholder', this.originalPlaceholder);
      }
      this.dock?.remove();
      this.announcement?.classList.remove('vss-announcement-docked');
      this.dock = null;
      this.announcement = null;
    }

    update() {
      if (this.destroyed || !this.header.isConnected) return;
      if (!this.media.matches) {
        this.restore();
        this.header.classList.remove('vss-compact-one-row', 'vss-compact-balanced');
        return;
      }
      const announcement = document.querySelector('.cc-announcement .announcement');
      const row = announcement?.querySelector('.announcement__col--left')?.parentElement;
      if (this.dock && (!this.dock.isConnected || this.announcement !== announcement)) this.restore();
      if (row && !this.dock) {
        this.announcement = announcement;
        this.dock = document.createElement('div');
        this.dock.className = 'vss-utility-actions';
        row.append(this.dock);
        announcement.classList.add('vss-announcement-docked');
      }
      if (this.dock) {
        this.move(this.header.querySelector('.secondary-nav__cta'));
        this.move(this.header.querySelector('[data-vss-help]'));
      }
      // All desktop sizes keep categories on a dedicated second row.
      this.header.classList.remove('vss-compact-one-row', 'vss-compact-balanced');
      this.orderActions();
      this.input?.setAttribute('placeholder', 'Search products, brands or part numbers...');
      this.alignResults();
    }

    orderActions() {
      const wishlist = this.icons?.querySelector('.xb-wishlist-header-auto-container');
      const cart = this.icons?.querySelector('#cart-icon');
      if (!wishlist || !cart || wishlist.parentNode !== this.icons) return;
      // Keep the original app node and its handlers. DOM order also sets keyboard order.
      if (wishlist.nextElementSibling !== cart) {
        if (!this.moves.has(wishlist)) this.move(wishlist, this.icons, cart);
        else this.icons.insertBefore(wishlist, cart);
      }
    }

    alignResults() {
      const headerBox = this.header.getBoundingClientRect();
      if (this.search) {
        const searchBox = this.search.getBoundingClientRect();
        this.header.style.setProperty('--vss-search-drop-top', `${Math.max(0, headerBox.bottom - searchBox.top)}px`);
        // Results remain inside the native form, positioned across the full header.
        // Measured edges also respect the editor iframe and the scrollbar width.
        this.header.style.setProperty('--vss-search-drop-left', `${headerBox.left - searchBox.left}px`);
        this.header.style.setProperty('--vss-search-drop-width', `${headerBox.width}px`);
        this.header.style.setProperty('--vss-header-viewport-bottom', `${Math.max(0, headerBox.bottom)}px`);
        this.header.style.setProperty('--vss-search-drop-height', `${Math.max(160, window.innerHeight - headerBox.bottom - 80)}px`);
      }
      const height = this.header.offsetHeight;
      if (height !== this.lastHeight) {
        this.lastHeight = height;
        if (typeof this.header.updateHeaderHeights === 'function') this.header.updateHeaderHeights();
        else if (typeof window.setHeaderHeight === 'function') window.setHeaderHeight();
      }
    }

    destroy() {
      this.destroyed = true;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.iconObserver.disconnect();
      this.resizeObserver.disconnect();
      this.media.removeEventListener('change', this.schedule);
      window.removeEventListener('resize', this.schedule);
      window.removeEventListener('scroll', this.onScroll);
      this.header.removeEventListener('load', this.schedule, true);
      this.search?.removeEventListener('focusin', this.schedule);
      this.menu?.removeEventListener('pointerover', this.closeSearch);
      this.menu?.removeEventListener('focusin', this.closeSearch);
      this.restore();
      this.header.classList.remove('vss-compact-one-row', 'vss-compact-balanced');
    }
  }

  let controller;
  const refresh = () => {
    controller?.destroy();
    const header = document.querySelector('#vss-store-header.vss-header--compact');
    controller = header ? new CompactHeader(header) : null;
  };
  window.VSSCompactHeader = { refresh };
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', event => {
    if (controller && (event.target.contains(controller.header) || event.target.contains(controller.announcement))) {
      controller.destroy();
      controller = null;
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
